import type { IEditorVariable } from "@bbj-types/utils";
import { type IAllProps, Editor as TinyMCEEditor } from "@tinymce/tinymce-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { useManualVariableHandlers } from "./hooks/useManualVariableHandlers";
import { getContentStyles, injectMenuStyles } from "./styles/editorStyles";
import { fromEditorHtmlToValue, toSpanTokens } from "./utils/contentPipeline";
import { registerVariableMenuButton } from "./utils/menuButton";

import "tinymce/tinymce";
import "tinymce/models/dom/model";

import "tinymce/icons/default";
import "tinymce/themes/silver";

import "tinymce/plugins/advlist";
import "tinymce/plugins/autoresize";
import "tinymce/plugins/charmap";
import "tinymce/plugins/emoticons";
import "tinymce/plugins/emoticons/js/emojis";
import "tinymce/plugins/fullscreen";
import "tinymce/plugins/image";
import "tinymce/plugins/insertdatetime";
import "tinymce/plugins/link";
import "tinymce/plugins/lists";
import "tinymce/plugins/nonbreaking";
import "tinymce/plugins/pagebreak";
import "tinymce/plugins/preview";
import "tinymce/plugins/quickbars";
import "tinymce/plugins/searchreplace";
import "tinymce/plugins/table";
import "tinymce/plugins/visualblocks";
import "tinymce/plugins/visualchars";
import "tinymce/plugins/wordcount";
import "./langs/fr_FR";
import { formatEmailHtmlContent, normalizeForTextOutput } from "./utils/textNormalization";

const PLUGINS = [
  "autoresize",
  "lists",
  "advlist",
  "wordcount",
  "visualblocks",
  "visualchars",
  "table",
  "insertdatetime",
  "nonbreaking",
  "pagebreak",
  "emoticons",
  "charmap",
  "fullscreen",
  "preview",
  "searchreplace",
  "image",
  "quickbars",
  "link",
] as const;

type TPlugins = (typeof PLUGINS)[number];
export interface IEditorProps extends Omit<IAllProps, "value" | "onChange"> {
  value?: string;
  onChange?: (html: string, text: string) => void;
  variableList?: IEditorVariable[];
  maxCharLimit?: number;
  excludePlugins?: TPlugins[];
}

export enum EditorToolbar {
  DEFAULT = "fontfamily fontsize | bold italic forecolor | alignment bullist numlist link outdent indent removeformat variable",
  EDITORV2 = "bold italic forecolor | alignment bullist numlist link outdent indent removeformat",
}

export const EditorV2 = ({
  value,
  onChange,
  variableList,
  maxCharLimit,
  init,
  excludePlugins,
  ...props
}: IEditorProps) => {
  const [editorValue, setEditorValue] = useState(toSpanTokens(value ?? "", variableList));
  const editorRef = useRef<Exclude<TinyMCEEditor["editor"], undefined> | null>(null);
  const variableListRef = useRef(variableList);
  const lastSerializedValueRef = useRef(value);
  const lastAppliedVariableListRef = useRef(variableList);
  const lastMaxCharLimitRef = useRef(maxCharLimit);

  useEffect(() => {
    variableListRef.current = variableList;
  }, [variableList]);

  const handleChange = useCallback(
    (_: string, editor: Exclude<TinyMCEEditor["editor"], undefined>) => {
      editorRef.current = editor;
      const html = editor.getContent({ format: "html" });

      const serialized = fromEditorHtmlToValue(html, variableList);
      const textOutput = normalizeForTextOutput(serialized);
      lastSerializedValueRef.current = serialized;
      const formattedHtml = formatEmailHtmlContent(serialized);

      onChange?.(formattedHtml, textOutput);
    },
    [onChange, variableList]
  );

  const syncEditorState = useCallback(
    (content: string, editor: Exclude<TinyMCEEditor["editor"], undefined>) => {
      setEditorValue(content);
      handleChange(content, editor);
    },
    [handleChange]
  );

  const { shouldSkipNextEditorChange, attachManualVariableHandlers } = useManualVariableHandlers({
    getVariableList: () => variableListRef.current,
    syncEditorState,
  });

  useEffect(() => {
    const hasExternalValueChange = value !== lastSerializedValueRef.current;
    const hasVariableListChange = variableList !== lastAppliedVariableListRef.current;
    const hasMaxCharLimitChange = maxCharLimit !== lastMaxCharLimitRef.current;

    if (!hasExternalValueChange && !hasVariableListChange && !hasMaxCharLimitChange) {
      return;
    }

    lastAppliedVariableListRef.current = variableList;
    lastMaxCharLimitRef.current = maxCharLimit;

    const rawValueToTokenize = hasExternalValueChange ? value : lastSerializedValueRef.current;
    const wordcount = editorRef?.current?.plugins?.wordcount;

    if (maxCharLimit && !wordcount?.selection) {
      return;
    }

    const textLength = wordcount?.selection?.getCharacterCount() || 0;
    if (typeof rawValueToTokenize === "undefined" || (maxCharLimit && textLength > (maxCharLimit || 0))) {
      return;
    }
    const tokens = toSpanTokens(rawValueToTokenize ?? "", variableList);
    if (hasExternalValueChange) {
      lastSerializedValueRef.current = value;
    }
    setEditorValue(tokens);
  }, [value, variableList, maxCharLimit]);

  const defaultToolbar = EditorToolbar.DEFAULT;
  const toolbar = props.toolbar ?? defaultToolbar;
  const plugins = PLUGINS.reduce<string[]>((acc, curr) => {
    if (!excludePlugins?.includes(curr) && curr !== "quickbars") {
      acc.push(curr);
    }
    return acc;
  }, []);

  useEffect(() => {
    if (editorRef.current) {
      if (props.disabled) {
        editorRef.current.contentDocument.body.style.opacity = "0.42";
      } else {
        editorRef.current.contentDocument.body.style.opacity = "1";
      }
    }
  }, [props.disabled]);

  return (
    <TinyMCEEditor
      value={editorValue}
      onEditorChange={(content, editor) => {
        const skipThisFrame = shouldSkipNextEditorChange();

        if (skipThisFrame) {
          return;
        }

        if (!maxCharLimit) {
          syncEditorState(content, editor);
          return;
        }
        const wordcount = editorRef?.current?.plugins?.wordcount?.selection;
        if (!wordcount) {
          return;
        }
        const textLength = wordcount?.getCharacterCount() || 0;
        if (textLength > maxCharLimit) {
          editor.setContent(toSpanTokens(value ?? "", variableList));
          return;
        }
        syncEditorState(content, editor);
      }}
      onInit={(_, editor) => {
        editorRef.current = editor;

        // Register variable menu button
        registerVariableMenuButton(editor, variableList);
      }}
      {...props}
      init={{
        ...init,
        plugins,
        toolbar: toolbar,
        setup: (editor) => {
          editor.on("init", () => {
            injectMenuStyles();
          });

          const cleanupManualHandlers = attachManualVariableHandlers(editor);
          editor.on("keydown", (e) => {
            if (e.shiftKey && e.key === "Enter") {
              editor.execCommand("InsertParagraph");
              e.preventDefault();
              return false;
            }
          });

          editor.on("beforeinput", (e) => {
            if (!maxCharLimit) {
              return true;
            }
            const currentLength = editor.getContent({ format: "text" }).length;
            const inputType = e.inputType || "";
            if (inputType.startsWith("insert") || inputType === "paste") {
              const dataLength = e.data?.length || 0;
              if (currentLength + dataLength > maxCharLimit) {
                e.preventDefault();
                return false;
              }
            }
            return true;
          });
          editor.on("paste", (e) => {
            if (!maxCharLimit) {
              return true;
            }
            const currentLength = editor.getContent({ format: "text" }).length;
            const pastedContent = e.clipboardData?.getData("text") ?? "";
            if (currentLength + pastedContent.length > maxCharLimit) {
              e.preventDefault();
              const remainingSpace = maxCharLimit - currentLength;
              if (remainingSpace > 0) {
                const truncatedContent = pastedContent.slice(0, remainingSpace);
                editor.insertContent(truncatedContent);
              }
              return false;
            }
            return true;
          });
          editor.on("BeforeExecCommand", (e) => {
            if (!maxCharLimit) {
              return true;
            }
            const currentLength = editor.getContent({ format: "text" }).length;
            if (currentLength >= maxCharLimit) {
              e.preventDefault();
              return false;
            }
            return true;
          });

          editor.on("remove", () => {
            cleanupManualHandlers();
          });
        },
        toolbar_groups: {
          alignment: {
            icon: "align-justify",
            tooltip: "Alignment",
            items: "alignleft aligncenter alignright alignjustify",
          },
        },
        base_url: window.location.href,
        branding: false,
        promotion: false,
        quickbars_insert_toolbar: false,
        font_size_formats: "8pt 10pt 12pt 14pt 16pt 18pt 24pt 36pt 48pt",
        font_family_formats:
          "Andale Mono=andale mono,times; Arial=arial,helvetica,sans-serif; Arial Black=arial black,avant garde; Book Antiqua=book antiqua,palatino; Comic Sans MS=comic sans ms,sans-serif; Courier New=courier new,courier; Georgia=georgia,palatino; Helvetica=helvetica; Impact=impact,chicago; Source Sans Pro=Source Sans Pro; Symbol=symbol; Tahoma=tahoma,arial,helvetica,sans-serif; Terminal=terminal,monaco; Times New Roman=times new roman,times; Trebuchet MS=trebuchet ms,geneva; Verdana=verdana,geneva; Webdings=webdings; Wingdings=wingdings,zapf dingbats",
        menubar: false,
        statusbar: false,
        icons: "material",
        icons_url: "https://fonts.bimbamjob.fr/tinymce/icons/material/icons.js",
        skin_url: "https://fonts.bimbamjob.fr/tinymce/skins/BBJMaterial",
        content_css: "https://fonts.bimbamjob.fr/tinymce/content.min.css",
        content_css_core: true,
        content_style: getContentStyles(),
        language: "fr_FR",
        entity_encoding: "raw",
        relative_url: true,
        document_base_url: undefined,
        theme: import.meta.env.NODE_ENV === "test" ? false : undefined,
        // Force line breaks to be converted to paragraphs
        forced_root_block: "p",
        forced_root_block_attrs: {},
        remove_trailing_brs: true,
        convert_newlines_to_brs: false,
        // Ensure proper paragraph handling
        br_in_pre: false,
        // Handle line breaks in content
        apply_source_formatting: true,
        verify_html: false,
        contextmenu: "",
      }}
    />
  );
};
